/**
 * Shared add-to-cart → wait for BOGOS gifts → checkout redirect.
 * BOGOS still mutates the cart before checkout. We wait on its events
 * (`fg-cart:auto-updated`, `fg-gifts:updated`, `fg-gifts:added`) and only
 * poll /cart.js as a fallback.
 */
(function initAmbrosiaFastCheckout() {
  'use strict';

  var FLAG = '__oseddCheckoutRedirectUntil';
  var CLASS_NAME = 'is-checkout-redirecting';
  var STYLE_ID = 'ambrosia-fast-checkout-style';
  var BOGO_EVENTS = ['fg-cart:auto-updated', 'fg-gifts:updated', 'fg-gifts:added'];
  var MAX_GIFT_WAIT_MS = 3000;
  var POLL_MS = 120;

  function ensureStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent =
      'html.is-checkout-redirecting cart-drawer,' +
      'html.is-checkout-redirecting cart-notification,' +
      'html.is-checkout-redirecting #freegifts-main-popup-container,' +
      'html.is-checkout-redirecting .sca-modal-fg{' +
      'display:none!important;visibility:hidden!important;pointer-events:none!important;' +
      '}' +
      'html.is-checkout-redirecting body{overflow:auto!important;}';
    document.head.appendChild(el);
  }

  function beginRedirecting(durationMs) {
    ensureStyles();
    window[FLAG] = Date.now() + Math.max(1000, Number(durationMs) || 8000);
    document.documentElement.classList.add(CLASS_NAME);
  }

  function endRedirecting() {
    delete window[FLAG];
    document.documentElement.classList.remove(CLASS_NAME);
  }

  function isRedirecting() {
    return Number(window[FLAG] || 0) > Date.now();
  }

  async function postJSON(url, bodyObj) {
    var res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(bodyObj || {}),
      credentials: 'same-origin',
      cache: 'no-store',
    });

    var contentType = res.headers.get('content-type') || '';
    var isJson = contentType.indexOf('application/json') !== -1;
    var data = isJson ? await res.json() : await res.text();

    if (!res.ok) {
      var msg =
        (data && (data.description || data.message)) ||
        (typeof data === 'string' ? data : 'Request failed');
      throw new Error(msg);
    }

    return data;
  }

  async function getCart() {
    var res = await fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) throw new Error('Unable to read cart');
    return res.json();
  }

  function itemsQty(items) {
    return (items || []).reduce(function (sum, item) {
      return sum + Math.max(1, Number(item && item.quantity) || 1);
    }, 0);
  }

  function attributesFromSectionConfig(sectionConfig) {
    if (!sectionConfig || !sectionConfig.bogoUrlParamsEnabled) return null;
    var raw = sectionConfig.bogoUrlParams || '';
    var bogo = window.AmbrosiaBogoUrlParams;
    var normalized = bogo && typeof bogo.normalize === 'function' ? bogo.normalize(raw) : String(raw).trim();
    if (!normalized) return null;

    var attrs = {};
    var hasAny = false;
    new URLSearchParams(normalized).forEach(function (value, key) {
      if (!key) return;
      attrs[key] = value;
      hasAny = true;
    });
    return hasAny ? attrs : null;
  }

  function mergeAttributes(bogoAttrs, extraAttributes) {
    var merged = {};
    var hasAny = false;
    [extraAttributes, bogoAttrs].forEach(function (source) {
      if (!source) return;
      Object.keys(source).forEach(function (key) {
        if (!key) return;
        var value = source[key];
        if (value == null || value === '') return;
        merged[key] = value;
        hasAny = true;
      });
    });
    return hasAny ? merged : null;
  }

  function getCheckoutUrl(sectionConfig, discountCodes) {
    var checkoutBase = (sectionConfig && sectionConfig.checkoutUrl) || '/checkout';
    var checkoutUrl = new URL(checkoutBase, window.location.origin);
    var currentParams = new URLSearchParams(window.location.search);
    var bogo = window.AmbrosiaBogoUrlParams;

    currentParams.forEach(function (value, key) {
      if (!key) return;
      if (bogo && typeof bogo.isInternalParam === 'function' && bogo.isInternalParam(key)) return;
      checkoutUrl.searchParams.set(key, value);
    });

    if (sectionConfig && sectionConfig.bogoUrlParamsEnabled) {
      var sanitized =
        bogo && typeof bogo.normalize === 'function'
          ? bogo.normalize(sectionConfig.bogoUrlParams)
          : String((sectionConfig && sectionConfig.bogoUrlParams) || '').trim();
      if (sanitized) {
        new URLSearchParams(sanitized).forEach(function (value, key) {
          if (key) checkoutUrl.searchParams.set(key, value);
        });
      }
    }

    if (discountCodes && discountCodes.length) {
      checkoutUrl.searchParams.set('discount', discountCodes.join(','));
    }

    if (checkoutUrl.origin === window.location.origin) {
      return checkoutUrl.pathname + checkoutUrl.search + checkoutUrl.hash;
    }
    return checkoutUrl.toString();
  }

  function watchNextCartAdd(onAdd) {
    var origFetch = window.fetch;
    if (typeof origFetch !== 'function') return function () {};

    function wrappedFetch(input, init) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var method = (init && init.method) || (input && input.method) || 'GET';
      var promise = origFetch.apply(this, arguments);
      if (/\/cart\/add\.js/i.test(String(url)) && String(method).toUpperCase() === 'POST') {
        promise
          .then(function (res) {
            if (res && res.ok) onAdd();
          })
          .catch(function () {});
      }
      return promise;
    }

    window.fetch = wrappedFetch;
    return function () {
      if (window.fetch === wrappedFetch) window.fetch = origFetch;
    };
  }

  function listenForBogoEvents(onHit) {
    function handler() {
      onHit();
    }
    BOGO_EVENTS.forEach(function (name) {
      document.addEventListener(name, handler);
    });
    return function () {
      BOGO_EVENTS.forEach(function (name) {
        document.removeEventListener(name, handler);
      });
    };
  }

  function waitForBogoGift(knownItemCount, alreadyHit) {
    if (alreadyHit) return Promise.resolve(true);

    return new Promise(function (resolve) {
      var settled = false;
      var unsub = [];

      function done(found) {
        if (settled) return;
        settled = true;
        for (var i = 0; i < unsub.length; i++) unsub[i]();
        resolve(!!found);
      }

      function onGiftSignal() {
        done(true);
      }

      unsub.push(listenForBogoEvents(onGiftSignal));
      unsub.push(watchNextCartAdd(onGiftSignal));

      function poll() {
        if (settled) return;
        fetch('/cart.js', {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'application/json' },
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (cart) {
            if (settled) return;
            if (cart && typeof cart.item_count === 'number' && cart.item_count > knownItemCount) {
              done(true);
            }
          })
          .catch(function () {});
      }

      poll();
      var pollId = window.setInterval(poll, POLL_MS);
      var timeoutId = window.setTimeout(function () {
        done(false);
      }, MAX_GIFT_WAIT_MS);

      unsub.push(function () {
        window.clearInterval(pollId);
        window.clearTimeout(timeoutId);
      });
    });
  }

  async function addItems(items) {
    return postJSON('/cart/add.js', { items: items || [] });
  }

  async function addThenCheckout(options) {
    options = options || {};
    var items = (options.items || []).filter(Boolean);
    if (!items.length) throw new Error('Nothing to add to cart.');

    var fallbackItems = (options.fallbackItems || []).filter(Boolean);
    var sectionConfig = options.sectionConfig || {};
    var discountCodes = options.discountCodes || [];
    var waitForGift = options.waitForGift === true;
    var clearCart = options.clearCart === true;
    var extraAttributes = options.extraAttributes || null;
    var bogoAttrs = attributesFromSectionConfig(sectionConfig);
    var attributes = mergeAttributes(bogoAttrs, extraAttributes);

    beginRedirecting();

    if (window.AmbrosiaBogoUrlParams) {
      window.AmbrosiaBogoUrlParams.apply(sectionConfig);
    }

    var giftHit = false;
    var stopBogoEvents = function () {};
    if (waitForGift && bogoAttrs) {
      stopBogoEvents = listenForBogoEvents(function () {
        giftHit = true;
      });
    }

    try {
      if (clearCart) {
        await postJSON('/cart/clear.js', {});
      }

      try {
        await addItems(items);
      } catch (err) {
        if (!fallbackItems.length) throw err;
        await addItems(fallbackItems);
        var rest = items.filter(function (item) {
          return fallbackItems.indexOf(item) === -1;
        });
        if (rest.length) {
          try {
            await addItems(rest);
          } catch (freebieErr) {
            console.warn('Freebies failed to add:', freebieErr);
          }
        }
      }

      var cart = null;
      if (attributes) {
        cart = await postJSON('/cart/update.js', { attributes: attributes });
      }

      if (waitForGift && bogoAttrs) {
        if (!cart) cart = await getCart();
        var knownCount = cart && typeof cart.item_count === 'number' ? cart.item_count : itemsQty(items);
        var requestedQty = itemsQty(items);
        var giftAlreadyPresent = clearCart && typeof cart.item_count === 'number' && cart.item_count > requestedQty;
        if (!giftHit && !giftAlreadyPresent) {
          await waitForBogoGift(knownCount, giftHit);
        }
      }
    } finally {
      stopBogoEvents();
    }

    if (window.AmbrosiaBogoUrlParams) {
      window.AmbrosiaBogoUrlParams.apply(sectionConfig);
    }

    window.location.href = getCheckoutUrl(sectionConfig, discountCodes);
  }

  window.AmbrosiaFastCheckout = {
    beginRedirecting: beginRedirecting,
    endRedirecting: endRedirecting,
    isRedirecting: isRedirecting,
    addThenCheckout: addThenCheckout,
    getCheckoutUrl: getCheckoutUrl,
  };
})();
